import { Children, type FC, type ReactNode } from "react";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { DeferMount } from "@/components/facility/DeferMount";

interface FacilitySectionProps {
    facilityId: number;
    children: ReactNode;
    /** Whether this facility has any unit rows. When false, the section shows
     *  the empty-state children and a "View Facility" button (instead of the
     *  units heading and the "See All Units" link). Falls back to inferring
     *  from children when omitted — but the empty state renders a no-units
     *  card as a child, so callers should pass this explicitly. */
    hasUnits?: boolean;
}

// Ticket WEB-704: the market template shows at most the first 3 features
// listed on a facility.
const MAX_FEATURES = 3;

/**
 * Per-facility card for the market template (WEB-704): a facility header
 * (banner image, linked name, address, reviews, first-3 feature pills) wrapping
 * the facility's unit rows (children), followed by a See All Units link to the
 * facility page. Purely presentational — everything comes from the facilityMap
 * entry the host passes via DisplayOptionContext. When the map has no entry for
 * the id, the children still render and the header/link are omitted.
 *
 * The visual treatment is ported from the theme's original market facility card
 * (WEB-704 salvage): a bordered white card with a banner image on top, a padded
 * body, and a divider above the stacked unit rows. The theme's design tokens
 * (neutral-light, card-foreground, …) don't exist in this standalone widget, so
 * they're translated to the widget's neutral utilities. Exact colors/spacing
 * still need a pass against the Design-1 XD mockup (plan Task 12).
 */
export const FacilitySection: FC<FacilitySectionProps> = ({
    facilityId,
    children,
    hasUnits: hasUnitsProp,
}) => {
    const { facilityMap } = useDisplayOptionContext();
    const facility = facilityMap[facilityId];

    const headingId = `facility-section-heading-${facilityId}`;
    const name = facility?.name;
    const url = facility?.url;
    const features = facility?.features?.slice(0, MAX_FEATURES) ?? [];
    // Compact rating (WEB-704 mockup): a single star + the numeric average,
    // shown inline after the address. Only when a numeric score is present.
    const reviewsScore = facility?.reviewsScore;
    const hasRating = typeof reviewsScore === "number";
    const hasUnits = hasUnitsProp ?? Children.count(children) > 0;

    return (
        <section
            className="facility-section tw:flex tw:flex-col tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:shadow-sm"
            aria-labelledby={name ? headingId : undefined}
        >
            {facility?.featuredImage && (
                <img
                    src={facility.featuredImage}
                    // Decorative: the facility name is shown in the body below.
                    alt=""
                    className="facility-section__image tw:w-full tw:aspect-[353/145] tw:object-cover tw:object-center"
                    loading="lazy"
                />
            )}
            <div className="facility-section__body tw:flex tw:flex-col tw:gap-3 tw:p-4 tw:md:p-5">
                {facility && (
                    <header className="facility-section__header tw:flex tw:flex-col tw:gap-1 tw:[&_h2]:m-0 tw:[&_p]:m-0">
                        {name && (
                            <h2
                                id={headingId}
                                className="facility-section__name tw:text-xl tw:font-semibold tw:leading-tight"
                            >
                                {url ? (
                                    <a
                                        href={url}
                                        className="facility-section__name-link tw:no-underline tw:hover:underline tw:text-inherit"
                                    >
                                        {name}
                                    </a>
                                ) : (
                                    name
                                )}
                            </h2>
                        )}
                        {(facility.address || hasRating) && (
                            <p className="facility-section__address-row tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-600">
                                {facility.address && (
                                    <span className="facility-section__address">
                                        {facility.address}
                                    </span>
                                )}
                                {/* Compact rating (mockup): "★ 4.9" inline after
                                    the address, separated by a short divider line
                                    (ported from the card's ::before separator).
                                    Star is decorative; the numeric value carries
                                    an accessible out-of-5 label. */}
                                {hasRating && (
                                    <span
                                        className={
                                            "facility-section__rating tw:inline-flex tw:items-center tw:gap-1 tw:whitespace-nowrap" +
                                            (facility.address
                                                ? " tw:border-l tw:border-gray-200 tw:pl-2"
                                                : "")
                                        }
                                        aria-label={`Rated ${reviewsScore} out of 5`}
                                    >
                                        <span
                                            className="facility-section__rating-star"
                                            aria-hidden="true"
                                        >
                                            ★
                                        </span>
                                        <span className="facility-section__rating-score">
                                            {reviewsScore}
                                        </span>
                                    </span>
                                )}
                            </p>
                        )}
                        {features.length > 0 && (
                            <ul
                                className="facility-section__features tw:flex tw:flex-wrap tw:gap-2 tw:list-none tw:m-0 tw:p-0"
                                aria-label="Facility features"
                            >
                                {features.map((feature) => (
                                    <li
                                        key={feature}
                                        className="facility-section__feature tw:inline-flex tw:items-center tw:bg-gray-100 tw:text-gray-700 tw:text-xs tw:font-medium tw:px-2.5 tw:py-1 tw:rounded-full"
                                    >
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </header>
                )}
                {/* Units block only renders when the facility has units. An
                    empty facility shows just the header + a View Facility
                    button (no "no units" table). Rows keep the shared
                    `.unit-table` grid so each `.unit-item`'s subgrid columns
                    resolve, matching the facility-page table; the outer market
                    container is a flex stack (`.unit-table--market`). */}
                {hasUnits && (
                    <div
                        className={
                            "facility-section__units" +
                            // Divider above the units, matching the card's
                            // border-t separator — only when there's a header.
                            (facility
                                ? " tw:mt-1 tw:border-t tw:border-gray-200 tw:pt-4"
                                : "")
                        }
                    >
                        <p className="facility-section__units-heading tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:m-0 tw:mb-3">
                            AVAILABLE STORAGE TYPES
                        </p>
                        {/* Defer mounting the unit rows (buttons, promo
                            dropdowns, tooltips) until the card nears the
                            viewport, so large markets (20-50+ facilities) don't
                            hydrate every facility's rows up front. Height is
                            reserved from the row count to limit scroll-jump. */}
                        <DeferMount minHeight={Children.count(children) * 96}>
                            <div className="unit-table">{children}</div>
                        </DeferMount>
                    </div>
                )}
                {url &&
                    (hasUnits ? (
                        <a
                            href={url}
                            className="facility-section__see-all tw:self-end tw:mt-1 tw:text-sm tw:font-medium tw:underline"
                            aria-label={
                                name
                                    ? `See all units at ${name}`
                                    : "See all units"
                            }
                        >
                            See All Units
                        </a>
                    ) : (
                        <a
                            href={url}
                            className="facility-section__view-facility tw:self-start tw:mt-1 tw:inline-flex tw:items-center tw:justify-center tw:rounded-md tw:px-4 tw:py-2 tw:text-sm tw:font-bold tw:no-underline"
                            aria-label={
                                name ? `View ${name}` : "View facility"
                            }
                        >
                            View Facility
                        </a>
                    ))}
            </div>
        </section>
    );
};
